import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { palette, Icon } from '@m12s/component-library';
import moment from 'moment';
import { fromNow } from 'lib/utils/date';
import { box } from 'lib/icons';
import { ElevatedCard } from 'lib/utils/styled';
import { useTranslation } from 'react-i18next';

import {
  getCurrentWorkOrderOp,
  getCurrentWorkOrderOpCompletionDeltaText,
  getCurrentWorkOrderOpTimeToComplete,
} from 'lib/selectors/getCurrentWorkOrderOp';
import { getOrderProgress } from 'lib/selectors/getOrderProgress';
import { useJobDisplayName } from 'lib/hooks/useJobDisplayName';
import { getWorkOrderOperationLaborTickets } from 'lib/selectors/getWorkOrderOperationLaborTickets';
import { getOperators } from 'lib/selectors/getOperators';
import { PickingTopCard, TopCardsRow } from './styled';
import { withNotApplicableFallback } from './helpers';

const LaborTicketsContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.5rem;
  gap: 0.75rem;
`;

const LtCard = styled.div`
  ${ElevatedCard}
  display: flex;
  align-items: center;
  padding: 0.5rem 1.125rem;
  background: ${palette.White};
  gap: 1rem;
`;

const LtContent = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 0.5rem;
  min-width: 0;
  font-size: 0.875rem;
  color: ${palette.Grey700};
  white-space: nowrap;
`;

const LtSep = styled.span`
  color: ${palette.Grey400};
  flex-shrink: 0;
`;

const LtText = styled.span`
  flex-shrink: 0;
`;

const LtPartsGroup = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
`;

const LtBadge = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 1.5rem;
  background: ${({ open }) => {
    return open ? palette.Green600 : palette.Grey300;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const LtEmpty = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${palette.Grey500};
  font-size: 0.9375rem;
  padding: 2rem;
`;

const DeltaText = styled.span`
  font-size: 0.8125rem;
  color: ${({ isLate }) => {
    return isLate ? palette.Red500 : palette.Green600;
  }};
`;

const formatTime = (timestamp) => {
  if (!timestamp) return '—';
  const m = moment(timestamp);
  if (m.isSame(moment(), 'day')) {
    return m.format('h:mm A');
  }
  return m.format('MMM D, h:mm A');
};

const formatDate = (timestamp) => {
  const momentTimestamp = moment(timestamp);
  if (momentTimestamp.isSame(moment(), 'day')) {
    return `Today ${momentTimestamp.format('h:mm A')}`;
  }
  return momentTimestamp.format('MMM D, h:mm A');
};

const Top = () => {
  const { t } = useTranslation();
  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const orderProgress = useSelector(getOrderProgress);
  const timeToComplete = useSelector(getCurrentWorkOrderOpTimeToComplete);
  const completionDeltaText = useSelector(
    getCurrentWorkOrderOpCompletionDeltaText
  );
  const { getOrderOpDisplayName } = useJobDisplayName();

  const orderOpDisplayName = useMemo(() => {
    return getOrderOpDisplayName(currentWorkOrderOp);
  }, [getOrderOpDisplayName, currentWorkOrderOp]);

  const { finishQuantity, workOrder } = currentWorkOrderOp || {};
  const partsRemaining = Math.max(finishQuantity - orderProgress, 0);
  const isLate = completionDeltaText?.startsWith('↓');

  let estimatedCompleteValue;
  // The early/late delta only describes an estimated date.
  let showDelta = false;
  if (orderProgress >= finishQuantity) {
    estimatedCompleteValue = t('Complete');
  } else if (timeToComplete === null) {
    estimatedCompleteValue = '—';
  } else {
    estimatedCompleteValue = formatDate(fromNow(timeToComplete));
    showDelta = Boolean(completionDeltaText);
  }

  return (
    <TopCardsRow>
      <PickingTopCard
        label="Job Number"
        value={workOrder?.workOrderId || 'None'}
        subValue={withNotApplicableFallback(orderOpDisplayName || null)}
      />
      <PickingTopCard
        label="Parts remaining"
        value={Math.max(partsRemaining || 0, 0)?.toLocaleString()}
        subValue={`${orderProgress?.toLocaleString() ||
          '—'} / ${finishQuantity?.toLocaleString() || '—'}`}
      />
      <PickingTopCard
        label="Earliest estimated complete"
        value={estimatedCompleteValue}
        subValue={
          showDelta ? (
            <DeltaText isLate={isLate}>{completionDeltaText}</DeltaText>
          ) : null
        }
      />
    </TopCardsRow>
  );
};

const LaborTicketsList = () => {
  const tickets = useSelector(getWorkOrderOperationLaborTickets);
  const operators = useSelector(getOperators);

  const operatorByErpId = useMemo(() => {
    const map = {};
    (operators || []).forEach((op) => {
      if (op.erpId) map[op.erpId] = op.name;
    });
    return map;
  }, [operators]);

  if (!tickets || tickets.length === 0) {
    return <LtEmpty>No labor tickets for this operation.</LtEmpty>;
  }

  return (
    <LaborTicketsContent>
      {tickets.map((ticket) => {
        const isOpen = ticket.state === 'OPEN';
        const timeStr = isOpen
          ? `${formatTime(ticket.clockIn)} · In Progress`
          : `${formatTime(ticket.clockIn)} – ${formatTime(ticket.clockOut)}`;
        const operatorName =
          operatorByErpId[ticket.personId] || ticket.personId || '—';
        const parts = (ticket.goodParts || 0) + (ticket.badParts || 0);

        return (
          <LtCard key={ticket.laborTicketId || ticket.laborTicketRef}>
            <LtContent>
              <LtText>{timeStr}</LtText>
              <LtSep>|</LtSep>
              <LtText>{operatorName}</LtText>
              <LtSep>|</LtSep>
              <LtPartsGroup>
                <span>Parts:</span>
                <span>{parts}</span>
              </LtPartsGroup>
            </LtContent>
            <LtBadge open={isOpen}>
              <Icon icon={box} size="0.75rem" color={palette.White} />
            </LtBadge>
          </LtCard>
        );
      })}
    </LaborTicketsContent>
  );
};

export const OrderOpTop = Top;

const PickingOrderOpContent = () => {
  return <LaborTicketsList />;
};

export default PickingOrderOpContent;
